import React, {useEffect, useMemo, useState} from 'react';
import HelpTooltip from '../HelpTooltip';
import OeTip from './OeTip';
import {calcParcels} from './api';
import {isExcluded} from './availability';
import {fmt, fmt0, qtyNum} from './format';

// Paketrechner im Versand-Assistenten (Mail Sebastian Fischinger 28.09.2026,
// Punkte 3 und 4).
//
// Er steht unter dem Frachtvergleich, der weiterhin nur Paletten rechnet, und
// zeigt zusätzlich, wie viele Pakete nach Kartontyp nötig wären und was sie
// kosten: Kundenpreis (aus den weclapp-Artikelpreisen des Vertriebswegs),
// DHL-Selbstkosten und die Differenz. Gerechnet wird im Server
// (`ParcelCalculator`); alle Annahmen stehen dort in `config/order_editor.php`.
//
// Die Länderregel gilt hier sichtbar: Dosen gehen nur nach Deutschland und in
// die Paketzonen 1 bis 3 per Paket, nach Malta und Zypern nie, in alle anderen
// Länder nur per Palette. Fehlt das Lieferland, steht dort „unbekannt" und
// nicht Deutschland.
//
// Unterdeckung ist gewollt und wird neutral gezeigt („Versandzuschuss
// Molotow"), nicht als Fehler.

const STATUS_TITEL = {
    gesperrt: 'Paketversand gesperrt',
    nur_palette: 'Nur per Palette',
    unbekannt: 'Lieferland unbekannt',
    keine_zone: 'Land keiner Paketzone zugeordnet',
};

/** „−2,83 €" bzw. „+0,01 €" — mit echtem Minuszeichen. */
function signed(v) {
    if (v == null) {
        return '—';
    }
    const betrag = fmt(Math.abs(v));
    return `${v < 0 ? '−' : '+'}${betrag} €`;
}

function inhaltText(dosen) {
    return Object.entries(dosen || {}).map(([ml, n]) => `${fmt0(n)}× ${ml} ml`).join(' + ');
}

/**
 * @param {object} order - geladener Beleg (Vertriebsweg).
 * @param {object[]} rows - aktuelle Positionen der Tabelle.
 * @param {object} stock - Bestandspaket (Lieferziel, freigegebene Versandartikel).
 * @param {string} orderRef
 * @param {boolean} canEdit
 * @param {(zeile: object) => void} onAdopt - legt die Versandkosten-Position an.
 */
export default function ParcelPanel({order, rows, stock, orderRef, canEdit, onAdopt}) {
    const [ergebnis, setErgebnis] = useState(null);
    const [laedt, setLaedt] = useState(false);
    const [fehler, setFehler] = useState(null);
    const [handPreis, setHandPreis] = useState('');

    const ziel = stock.shipTo || null;

    // Positionen für den Rechner: dieselben, die auch in Gewicht und
    // Palettenschätzung eingehen (ohne gelöschte, ohne Restzeilen der
    // Rabatt-Teilung, ohne Positionen „Keine Lieferung").
    const nutzlast = useMemo(() => ({
        iso: (ziel && ziel.countryCode) || null,
        country: (ziel && ziel.country) || null,
        channel: (order && order.salesChannel) || null,
        items: (rows || [])
            .filter((r) => !r.del && !r._new && r.articleId && !isExcluded(r))
            .map((r) => ({
                id: r.id != null ? String(r.id) : null,
                parentItemId: r.parentItemId != null ? String(r.parentItemId) : null,
                articleId: String(r.articleId),
                quantity: qtyNum(r.quantity),
                unitPrice: r.unitPrice != null && r.unitPrice !== '' ? qtyNum(r.unitPrice) : null,
                discountPercentage: qtyNum(r.discountPercentage),
            })),
    }), [rows, ziel, order]);

    const signatur = JSON.stringify(nutzlast);

    useEffect(() => {
        let abgebrochen = false;
        setLaedt(true);

        const timer = window.setTimeout(() => {
            calcParcels(orderRef, nutzlast)
                .then((d) => {
                    if (!abgebrochen) {
                        setErgebnis(d);
                        setFehler(null);
                    }
                })
                .catch((e) => {
                    if (!abgebrochen) {
                        setErgebnis(null);
                        setFehler((e && e.response && e.response.data && e.response.data.error)
                            || 'Der Paketrechner konnte nicht rechnen (weclapp nicht erreichbar?).');
                    }
                })
                .finally(() => {
                    if (!abgebrochen) {
                        setLaedt(false);
                    }
                });
        }, 700);

        return () => {
            abgebrochen = true;
            window.clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signatur, orderRef]);

    if (!order) {
        return null;
    }

    const artikelListe = stock.shippingArticles || [];
    const r = ergebnis;
    const brutto = !!(r && r.gross);

    const freigegeben = r && r.allowed
        ? artikelListe.find((a) => String(a.articleId) === String(r.article.articleId))
        : null;

    const handZahl = parseFloat(String(handPreis).replace(',', '.'));
    const preis = r && r.allowed
        ? (r.priceEach != null ? r.priceEach : (handZahl >= 0 ? handZahl : null))
        : null;
    const anzahl = r && r.allowed ? r.totals.packages : 0;

    const adopt = () => {
        if (!r || !freigegeben || preis == null || anzahl < 1) {
            return;
        }
        onAdopt({
            articleId: freigegeben.articleId,
            articleNumber: freigegeben.articleNumber,
            name: freigegeben.name,
            qty: anzahl,
            pricePer: +preis.toFixed(2),
        });
    };

    const hinweise = (r && r.notes) || [];
    const warnungen = hinweise.filter((h) => h.level === 'warn');
    const infos = hinweise.filter((h) => h.level !== 'warn');

    let adoptTip = 'Merkt die Versandkosten-Position vor (Paket-Artikel, Anzahl und Kundenpreis je Paket). '
        + 'Sie erscheint unten in der Liste und geht erst mit „Speichern" nach weclapp.';
    if (r && r.allowed && !freigegeben) {
        adoptTip = 'Dieser Versandartikel ist nicht freigegeben. Bitte auf der Pflegeseite der Stammdaten '
            + '(Versandartikel) aufnehmen.';
    } else if (r && r.allowed && preis == null) {
        adoptTip = 'Es ist kein Zonenpreis gepflegt — bitte unten den Preis je Paket von Hand eintragen.';
    }

    return (
        <div className="frachtbox parcelbox" id="oe-parcelbox">
            <div className="fb-h">
                📦 Paketrechner <span className="muted">(Dosen per Paket)</span>
                <HelpTooltip
                    id="oe-pc-help"
                    placement="bottom"
                    size={0.7}
                    detail={<>
                        Die Aufteilung nimmt möglichst wenige Kartons, bei Gleichstand möglichst wenig Leerraum; das
                        Gewicht je Paket einschließlich Karton ist die harte Grenze (Deutschland 31,5 kg, EU 21 kg).
                        Gewichte und Preise kommen aus weclapp, die Zone aus dem Kommentar des Zonen-Artikels.
                    </>}
                >
                    Zeigt zusätzlich zu den Paletten, wie viele Pakete nach Kartontyp nötig wären und was sie
                    kosten. Gespeichert wird nichts — erst „übernehmen" merkt die Position vor.
                </HelpTooltip>
                {laedt ? <span className="muted"> rechnet …</span> : null}
            </div>

            {fehler ? <div className="fb-err">{fehler}</div> : null}

            {r && !r.allowed ? (
                <div className="prodwarn" style={{margin: 0}}>
                    <OeTip tip={'Dosen gehen per Paket nur nach Deutschland und in die Paketzonen 1 bis 3 (ohne '
                        + 'Malta und Zypern). Für alle anderen Länder bleibt nur die Palette — den Preis dafür zeigt '
                        + 'der Frachtvergleich oben.'}>
                        <b>🚫 {STATUS_TITEL[r.status] || 'Kein Paketversand'}</b>
                    </OeTip>
                    {' — '}{r.message}
                    {(r.notes || []).map((h, i) => <div key={i} className="pc-note">{h.text}</div>)}
                </div>
            ) : null}

            {r && r.allowed ? (
                <>
                    <div className="pc-sub">
                        <OeTip
                            tip="Lieferland und Paketzone des Auftrags."
                            detail={'Die Länder je Zone stehen im ältesten Kommentar des Zonen-Artikels in weclapp; '
                                + 'Änderungen dort gelten nach spätestens fünf Minuten (oder sofort nach „Speichern" '
                                + 'auf der Pflegeseite der Stammdaten).'}
                        >
                            <span className="infochip">
                                🌍 {r.iso} → <b>{r.status === 'de' ? 'Inland' : `Zone ${r.zone}`}</b>
                            </span>
                        </OeTip>
                        <OeTip tip={'Der Versandartikel, der je Paket am Auftrag angelegt wird. Der Preis kommt aus '
                            + 'den Artikelpreisen für den Vertriebsweg des Auftrags.'}>
                            <span>
                                Artikel <b>{r.article.articleNumber}</b>
                                {r.priceEach != null ? (
                                    <>
                                        {' '}· <b>{fmt(r.priceEach)} € {brutto ? 'brutto' : 'netto'}</b> je Paket
                                        {brutto ? <span className="muted"> ({fmt(r.priceNetEach)} € netto)</span> : null}
                                    </>
                                ) : null}
                            </span>
                        </OeTip>
                    </div>

                    {anzahl > 0 ? (
                        <>
                            <div className="pc-chips">
                                {r.cartonCounts.map((k) => (
                                    <OeTip
                                        key={k.carton}
                                        tip={`${k.label} (${k.number}): ${k.count} Paket(e), Gewicht je Paket `
                                            + `${k.weights.map((w) => fmt(w)).join(' · ')} kg einschließlich Karton.`}
                                    >
                                        <span className="pc-chip"><b>{fmt0(k.count)}×</b> {k.label}</span>
                                    </OeTip>
                                ))}
                            </div>

                            <table className="sctable" style={{marginTop: 4}}>
                                <thead>
                                    <tr>
                                        <th><OeTip tip="Laufende Nummer des Pakets."><span>Paket</span></OeTip></th>
                                        <th><OeTip tip="Kartontyp und Artikelnummer des Kartons in weclapp."><span>Karton</span></OeTip></th>
                                        <th><OeTip tip="Dosen im Paket nach Größe."><span>Inhalt</span></OeTip></th>
                                        <th className="num"><OeTip tip="Gewicht des Pakets einschließlich Karton. Beim letzten Paket kommen Nicht-Dosen (Marker, Caps, Sets …) dazu."><span>Gewicht</span></OeTip></th>
                                        <th className="num"><OeTip tip="Kundenpreis je Paket netto (bei PG1 wird der Bruttopreis durch den Steuerfaktor geteilt). Der Preis ist unabhängig vom Gewicht."><span>Kunde netto</span></OeTip></th>
                                        <th className="num"><OeTip tip="Unsere DHL-Selbstkosten je Paket netto (Grundpreis + Energiezuschlag + Maut). In Deutschland richtet sich die Stufe nach dem Gewicht des Pakets. Diese Zahlen gibt es nicht in weclapp."><span>Selbstkosten</span></OeTip></th>
                                        <th className="num"><OeTip tip="Kundenpreis netto minus Selbstkosten. Ein Minus ist gewollt und kein Fehler — es zeigt nur, was Molotow zuschießt."><span>Differenz</span></OeTip></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {r.packages.map((p) => (
                                        <tr key={p.nr}>
                                            <td>{p.nr}</td>
                                            <td>{p.cartonLabel} <span className="muted">{p.cartonNumber}</span></td>
                                            <td>{inhaltText(p.cans)}</td>
                                            <td className="num">{fmt(p.weight)} kg</td>
                                            <td className="num">{p.customerNet != null ? `${fmt(p.customerNet)} €` : '—'}</td>
                                            <td className="num">
                                                {p.selfCost ? (
                                                    <OeTip tip={`Grundpreis ${fmt(p.selfCost.base)} € + Energiezuschlag ${fmt(p.selfCost.energy)} € + Maut ${fmt(p.selfCost.toll)} €`}>
                                                        <span>{fmt(p.selfCost.total)} €</span>
                                                    </OeTip>
                                                ) : '—'}
                                            </td>
                                            <td className="num pc-diff">{signed(p.difference)}</td>
                                        </tr>
                                    ))}
                                    <tr className="sc-total">
                                        <td colSpan={3}>Summe · {fmt0(r.totals.packages)} Paket(e)</td>
                                        <td className="num">{fmt(r.totals.weight)} kg</td>
                                        <td className="num">{r.totals.customerNet != null ? `${fmt(r.totals.customerNet)} €` : '—'}</td>
                                        <td className="num">{r.totals.selfCost != null ? `${fmt(r.totals.selfCost)} €` : '—'}</td>
                                        <td className="num pc-diff">{signed(r.totals.difference)}</td>
                                    </tr>
                                </tbody>
                            </table>

                            {r.totals.differencePerPackage != null && r.totals.differencePerPackage < 0 ? (
                                <OeTip tip="Der Kundenpreis liegt teilweise bewusst unter unseren Selbstkosten. Das ist gewollt; die Zahl ist nur zur Information.">
                                    <div className="pc-note pc-diff">
                                        Versandzuschuss Molotow: {signed(r.totals.differencePerPackage)} je Paket
                                    </div>
                                </OeTip>
                            ) : null}

                            {r.priceMissing ? (
                                <div className="pc-manual">
                                    <OeTip tip={`Für diesen Vertriebsweg ist in weclapp kein Preis gepflegt. Tragen Sie den Preis je Paket ${brutto ? 'brutto' : 'netto'} von Hand ein — er wird dann mit „übernehmen" verwendet.`}>
                                        <span>Zonenpreis fehlt — Preis je Paket {brutto ? 'brutto' : 'netto'} €</span>
                                    </OeTip>
                                    <OeTip tip="Preis je Paket von Hand. Wird nicht gespeichert, bis die Position vorgemerkt ist.">
                                        <input
                                            className="fb-in"
                                            inputMode="decimal"
                                            autoComplete="off"
                                            aria-label="Zonenpreis je Paket von Hand"
                                            placeholder="z. B. 12,50"
                                            value={handPreis}
                                            disabled={!canEdit}
                                            onChange={(e) => setHandPreis(e.target.value)}
                                        />
                                    </OeTip>
                                </div>
                            ) : null}

                            <div className="pc-adopt">
                                {canEdit ? (
                                    <OeTip tip={adoptTip}>
                                        <span>
                                            <button
                                                type="button"
                                                className="actbtn"
                                                disabled={!freigegeben || preis == null}
                                                onClick={adopt}
                                            >
                                                ✓ {fmt0(anzahl)}× {r.article.articleNumber} übernehmen
                                            </button>
                                        </span>
                                    </OeTip>
                                ) : null}
                                {preis != null ? (
                                    <span className="muted">
                                        je {fmt(preis)} € {brutto ? 'brutto' : 'netto'}
                                        {brutto ? ' (PG1: weclapp rechnet hier mit Bruttopreisen)' : ''}
                                    </span>
                                ) : null}
                            </div>
                        </>
                    ) : null}

                    {r.nonCans && r.nonCans.length ? (
                        <OeTip tip={'Marker, Refills, Caps, Sticker und Sets zählen nicht als Dosen. Sie werden hier gesondert gelistet; '
                            + 'ihr Gewicht kommt auf das letzte Paket.'}>
                            <div className="pc-note">
                                <b>Nicht-Dosen</b> (Gewicht auf dem letzten Paket):
                                <ul className="pc-list">
                                    {r.nonCans.map((n, i) => (
                                        <li key={i}>
                                            {fmt0(n.quantity)}× {n.articleNumber} {n.name}
                                            {n.kind === 'set' ? ' (Set, eine Einheit)' : ''}
                                            {' — '}{n.weightEach == null ? 'Gewicht fehlt' : `${fmt(n.weight)} kg`}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </OeTip>
                    ) : null}

                    {r.insurance && r.insurance.goodsValue > 0 && (r.insurance.perPackage != null || r.insurance.aboveLimit) ? (
                        <OeTip tip={'Hinweis auf Basis des Warenwerts (netto) dieses Auftrags: 500 bis 2.500 € → +10 € je Paket, '
                            + '2.500 bis 25.000 € → +50 € je Paket. Es wird nichts automatisch gebucht.'}>
                            <div className="pc-note">
                                <b>Transportversicherung</b> (nur Hinweis): Warenwert {fmt(r.insurance.goodsValue)} € netto
                                {r.insurance.perPackage != null
                                    ? <> → +{fmt(r.insurance.perPackage)} € je Paket, zusammen +{fmt(r.insurance.total)} €</>
                                    : ' — über der höchsten Stufe, bitte individuell klären'}
                                {r.insurance.articleNumber ? ` · Artikel ${r.insurance.articleNumber}` : ''}.
                            </div>
                        </OeTip>
                    ) : null}

                    {warnungen.map((h, i) => <div key={`w${i}`} className="pc-note warn">{h.text}</div>)}
                    {infos.map((h, i) => <div key={`i${i}`} className="pc-note">{h.text}</div>)}
                </>
            ) : null}
        </div>
    );
}
